import React, { useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { participantsApi, runsApi } from '../services/api';
import { Participant, Run, PaginatedResponse } from '../services/types';
import { useApiQuery } from '../hooks/useApiQuery';
import { TableSkeleton } from '../components/SkeletonLoaders';
import ErrorMessage from '../components/ErrorMessage';

// NC-1074: compact "von–bis" range for the Schulung column. Prefers the precise
// UTC-ISO fields (NC-457) and falls back to the legacy date-only fields; returns
// '' when neither bound is known so the caller can hide it.
export function formatRunDateRange(run?: Run): string {
  if (!run) return '';
  const fmt = (v?: string): string => {
    if (!v) return '';
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('de-DE');
  };
  const start = fmt(run.startDateTimeUtc ?? run.scheduledStart);
  const end = fmt(run.endDateTimeUtc ?? run.expireOn);
  if (start && end) return start === end ? start : `${start} – ${end}`;
  return end || start || '';
}

export default function ParticipantsPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [runFilter, setRunFilter] = useState<string>('all');
  const { data: participants, loading, error, refetch } = useApiQuery<Participant[]>(
    () => participantsApi.list()
  );

  // NC-1073: participants derive from runs[].accounts (all non-deleted runs,
  // incl. expired history), so the Schulung lookup needs view=all — the /runs
  // default of "active" would drop labels for expired Schulungen. A lookup
  // failure degrades gracefully: dropdown offers only "Alle Schulungen" and
  // the column falls back to raw RunIds.
  const { data: runsPage } = useApiQuery<PaginatedResponse<Run>>(
    () => runsApi.list({ view: 'all', pageSize: 500 })
  );

  const runById = useMemo(() => {
    const map = new Map<string, Run>();
    for (const run of runsPage?.items ?? []) {
      if (run.id) map.set(run.id, run);
      if (run.runId) map.set(run.runId, run);
    }
    return map;
  }, [runsPage]);

  const sortedRuns = useMemo(
    () =>
      [...(runsPage?.items ?? [])].sort((a, b) =>
        (b.scheduledStart ?? b.createdAtUtc ?? '').localeCompare(
          a.scheduledStart ?? a.createdAtUtc ?? ''
        )
      ),
    [runsPage]
  );

  const isFiltered = searchTerm !== '' || runFilter !== 'all';

  const filteredParticipants = (participants || []).filter((p) => {
    const needle = searchTerm.toLowerCase();
    const matchesSearch =
      searchTerm === '' ||
      `${p.firstName} ${p.lastName}`.toLowerCase().includes(needle) ||
      p.email.toLowerCase().includes(needle) ||
      (p.externalEmail?.toLowerCase().includes(needle) ?? false) ||
      (p.company?.toLowerCase().includes(needle) ?? false);
    const matchesRun = runFilter === 'all' || (p.runsAttended ?? []).includes(runFilter);
    return matchesSearch && matchesRun;
  });

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">Teilnehmer</Typography>
        <Tooltip title="Aktualisieren">
          <IconButton onClick={refetch} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <ErrorMessage
          message={error}
          title="Teilnehmer konnten nicht geladen werden"
          onRetry={refetch}
        />
      )}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" gap={2} flexWrap="wrap">
            <TextField
              sx={{ flex: 1, minWidth: 240 }}
              label="Suche"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Name, Email, Email extern oder Firma suchen..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl sx={{ minWidth: 240 }}>
              <InputLabel id="participants-run-filter-label">Schulung</InputLabel>
              <Select
                labelId="participants-run-filter-label"
                label="Schulung"
                value={runFilter}
                onChange={(e) => setRunFilter(e.target.value)}
              >
                <MenuItem value="all">Alle Schulungen</MenuItem>
                {sortedRuns.map((run) => (
                  <MenuItem key={run.id} value={run.id}>
                    {run.course} ({run.id})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {loading && !participants ? (
        <TableSkeleton rows={5} columns={7} />
      ) : (
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Vorname</TableCell>
                <TableCell>Nachname</TableCell>
                <TableCell>Email (intern)</TableCell>
                <TableCell>Email (extern)</TableCell>
                <TableCell>Firma</TableCell>
                <TableCell>Schulung</TableCell>
                <TableCell>Workload</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredParticipants.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="text.secondary">
                      {isFiltered
                        ? 'Keine Teilnehmer gefunden'
                        : 'Keine Teilnehmer vorhanden'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredParticipants.map((participant) => {
                  const attended = participant.runsAttended ?? [];
                  return (
                  <TableRow key={participant.id} hover>
                    <TableCell>{participant.firstName || '-'}</TableCell>
                    <TableCell>{participant.lastName || '-'}</TableCell>
                    <TableCell>{participant.email || '-'}</TableCell>
                    <TableCell>{participant.externalEmail || '-'}</TableCell>
                    <TableCell>{participant.company || '-'}</TableCell>
                    <TableCell>
                      {attended.length === 0 ? (
                        '-'
                      ) : (
                        <Box display="flex" flexDirection="column" gap={0.5}>
                          {attended.map((runId) => {
                            const run = runById.get(runId);
                            const range = formatRunDateRange(run);
                            return (
                              <Box key={runId} title={runId}>
                                <Typography variant="body2">
                                  {run?.course ?? runId}
                                </Typography>
                                {range && (
                                  <Typography variant="caption" color="text.secondary">
                                    {range}
                                  </Typography>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const chips = attended
                          .map((runId) => {
                            const run = runById.get(runId);
                            const wl = String(run?.workloadId ?? run?.workload ?? '');
                            return { runId, wl };
                          })
                          .filter((x) => x.wl !== '');
                        return chips.length === 0 ? (
                          '-'
                        ) : (
                          <Box display="flex" gap={0.5} flexWrap="wrap">
                            {chips.map(({ runId, wl }) => (
                              <Chip key={runId} size="small" label={wl} title={runId} />
                            ))}
                          </Box>
                        );
                      })()}
                    </TableCell>
                  </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {filteredParticipants.length > 0 && (
          <Box p={2}>
            <Typography variant="body2" color="text.secondary">
              {filteredParticipants.length} Teilnehmer
              {isFiltered && ` (gefiltert von ${(participants || []).length})`}
            </Typography>
          </Box>
        )}
      </Card>
      )}
    </Box>
  );
}
